import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { MessageSquare, Eye, Heart, Bookmark, BookOpen, Languages, Clock, CheckCircle2, PencilLine, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { getCategoryMeta } from "@/lib/categories";
import { getTopicGenerationState } from "@/lib/topic-generation-state";
import { UserAvatar } from "@/components/common/UserAvatar";
import { metaChipClass, slateCardClass } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { formattersFor, useLanguage } from "@/i18n";
import type { Json } from "@/integrations/supabase/types";
import { useUserProfile } from "@/hooks/use-user-profiles";
import { useTranslation } from "react-i18next";
/**
 * Simplified session type for topic display.
 * Supports both HistorySession and PublicSession formats.
 */
export interface TopicDisplaySession {
    id: string;
    topic: string;
    slug?: string;
    created_at: string;
    category?: string | null;
    description?: string;
    banner_url?: string | null;
    view_count?: number;
    like_count?: number;
    comment_count?: number;
    creator_email?: string;
    creator_display_name?: string;
    creator_avatar_url?: string | null;
    user_id?: string;
    questions?: Json;
    vocabulary?: Json;
    speaking_guide?: Json;
}

export interface TopicCardProps {
    session: TopicDisplaySession;
    onView: (session: TopicDisplaySession) => void;
    onPractice?: (session: TopicDisplaySession) => void;
    onBookmark?: (session: TopicDisplaySession) => void;
    showViewButton?: boolean;
    progress?: number;
    variant?: "default" | "personal" | "public";
    headerLeft?: React.ReactNode;
    headerRight?: React.ReactNode;
    headerLeftExtra?: React.ReactNode;
    footerExtra?: React.ReactNode;
    hideStats?: boolean;
    compact?: boolean;
    badgeExtra?: React.ReactNode;
    hideCreator?: boolean;
    bannerAspect?: string;
    /**
     * Personal topics only. When the topic is the learner's own, the card can
     * say a generation run stopped early and offer a way to finish it. Public
     * cards never pass this — you cannot regenerate someone else's topic.
     */
    onFinishGeneration?: (session: TopicDisplaySession) => void;
    /** A generation run for this topic is live right now, so it is not "unfinished". */
    isGenerating?: boolean;
}

function getContentCounts(questions?: Json, vocabulary?: Json) {
    const qCount = Array.isArray(questions) ? questions.length : 0;
    const vCount = Array.isArray(vocabulary) ? vocabulary.length : 0;
    return { qCount, vCount };
}

const TopicCard = ({
    session,
    onView,
    onPractice,
    onBookmark,
    showViewButton = false,
    progress,
    variant = "default",
    headerLeft,
    headerRight,
    headerLeftExtra,
    footerExtra,
    hideStats = false,
    compact = false,
    badgeExtra,
    hideCreator = false,
    bannerAspect = "aspect-[16/9]",
    onFinishGeneration,
    isGenerating = false,
}: TopicCardProps) => {
    const { t } = useTranslation();
    const format = useLanguage()?.format ?? formattersFor("en");
    const category = (session.category as string | undefined) || "General";
    const { icon: CategoryIcon, iconColor } = getCategoryMeta(category);

    const { qCount, vCount } = useMemo(
        () => getContentCounts(session.questions, session.vocabulary),
        [session.questions, session.vocabulary]
    );

    // Only personal cards are allowed to flag an unfinished run; a public topic
    // is someone else's and there is nothing here the reader could regenerate.
    const showGenerationStatus = !!onFinishGeneration || isGenerating;
    const generationState = getTopicGenerationState(session);
    const generationGapLabel = generationState.isBlank
        ? t("pages.myTopics.nothingGeneratedYet", "nothing generated yet")
        : generationState.missing[0] === "questions"
            ? t("pages.myTopics.unfinishedCardQuestions", "questions missing")
            : t("pages.myTopics.unfinishedCardVocabulary", "vocabulary missing");

    // Fetch creator profile if display name is missing but user_id is available
    const shouldFetchCreator = !session.creator_display_name && !!session.user_id;
    const { data: creatorProfile } = useUserProfile(shouldFetchCreator ? session.user_id! : null);
    const creatorName = session.creator_display_name || creatorProfile?.display_name || "";

    // One destination for the card. The body and the title go to the same
    // place, so a pointer and a keyboard get the same outcome — the card used
    // to open a "Choose Mode" sheet from its body while its title navigated,
    // which meant the only focusable element was the quieter of two answers,
    // and a keyboard user could never reach Start Practice at all.
    const canPractise = !!onPractice && !!session.slug;
    const cardHref = session.slug
        ? canPractise
            ? `/session/${session.slug}/practice`
            : `/session/${session.slug}`
        : null;

    // The card's accessible name and keyboard target.
    const titleNode = cardHref ? (
        <Link
            to={cardHref}
            onClick={(e) => e.stopPropagation()}
            className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
            {session.topic}
        </Link>
    ) : (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onView(session);
            }}
            className="rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
            {session.topic}
        </button>
    );

    const handleCardClick = () => {
        if (canPractise) onPractice?.(session);
        else onView(session);
    };

    const handleBookmark = (e: React.MouseEvent) => {
        e.stopPropagation();
        onBookmark?.(session);
    };

    return (
        <>
            <Card
                className={cn(
                    slateCardClass,
                    // flex-col + h-full is what lets the content column stretch
                    // and the creator row sit on the card's bottom edge, so
                    // cards in a grid row line up instead of ending ragged.
                    "group flex h-full cursor-pointer flex-col overflow-hidden transition-all duration-200 ease-out",
                    "hover:border-primary/40 hover:shadow-md"
                )}
                onClick={handleCardClick}
            >
                {/* Banner - Top.
                    A topic's own banner, or a typographic plate. It used to fall
                    back to the *category's* stock photo, so the whole first row
                    showed the same laptop-and-hands and nine cards read
                    "GENERAL" over it — a card whose job is to be *that* topic
                    has to look like it. Home already follows this rule. */}
                <div className={cn("relative w-full overflow-hidden rounded-t-2xl", bannerAspect)}>
                    {session.banner_url ? (
                        <>
                            <img
                                src={session.banner_url}
                                alt=""
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                loading="lazy"
                            />
                            {/* Subtle bottom gradient for overlay legibility */}
                            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-stone-950/45 to-transparent pointer-events-none" />
                        </>
                    ) : (
                        /* The same dot texture the page hero uses, so a
                           placeholder cover reads as paper rather than a broken
                           image. The glyph is a watermark in the corner, never
                           behind a word.

                           It carries no text: putting the topic in here made a
                           banner-less card a different object from a card with a
                           photo — 231px against 287px, serif against sans, and
                           the glyph's strokes crossed the title. One anatomy for
                           every card; the title lives below the cover for both. */
                        <div
                            className="h-full w-full overflow-hidden bg-stone-100/70 dark:bg-stone-800/40"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.16) 1px, transparent 0)",
                                backgroundSize: "12px 12px",
                            }}
                        >
                            <CategoryIcon
                                className={cn("absolute -bottom-2 -right-1 h-14 w-14 opacity-[0.14]", iconColor)}
                                aria-hidden="true"
                            />
                        </div>
                    )}

                    {/* Glassy slate category chip (top-left) */}
                    <span
                        className={cn(
                            "absolute top-2 left-2 z-10",
                            "inline-flex items-center gap-1.5 px-2 py-1 rounded-md",
                            "bg-white/90 dark:bg-stone-900/90 backdrop-blur-sm",
                            "border border-stone-200/80 dark:border-stone-700/60",
                            "text-[11px] font-semibold uppercase tracking-wider",
                            "text-stone-700 dark:text-stone-200"
                        )}
                    >
                        <CategoryIcon className={cn("w-3 h-3", iconColor)} />
                        <span>{t(`pages.topics.categories.${category}`, { defaultValue: category })}</span>
                    </span>

                    {/* Header left (optional) — stacks below the category chip on the banner */}
                    {headerLeft && (
                        <div
                            className="absolute top-10 left-2 z-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {headerLeft}
                        </div>
                    )}

                    {/* Top-right cluster: headerRight + bookmark */}
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
                        {onBookmark && (
                            <button
                                onClick={handleBookmark}
                                className={cn(
                                    "inline-flex items-center justify-center h-7 w-7 rounded-md",
                                    "bg-white/90 dark:bg-stone-900/90 backdrop-blur-sm",
                                    "border border-stone-200/80 dark:border-stone-700/60",
                                    "text-stone-500 dark:text-stone-400",
                                    "opacity-70 hover:opacity-100 hover:text-primary",
                                    "transition-all duration-200"
                                )}
                                aria-label="Bookmark topic"
                            >
                                <Bookmark className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {headerRight && (
                            <div onClick={(e) => e.stopPropagation()}>
                                {headerRight}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Info - Below Banner */}
                <div className="flex flex-1 flex-col p-3 sm:p-4">
                    {/* Title (most prominent, first) */}
                    {/* The title is the card's keyboard entry point. The
                        surface's onClick is a pointer convenience; a DIV with a
                        handler is not reachable, has no role and no name, so a
                        keyboard user could not enter the grid at all (measured:
                        40 Tab presses never reached a card). A link is the
                        canonical affordance for a grid of destinations, and it
                        gives the card a real accessible name for free. */}
                    <h3
                        id={`topic-title-${session.id}`}
                        className={cn(
                            "text-sm sm:text-base font-semibold leading-snug line-clamp-2 min-h-[2lh] mb-1.5 md:mb-2",
                            "text-foreground group-hover:text-primary",
                            "transition-colors duration-200"
                        )}
                    >
                        {titleNode}
                    </h3>

                    {/* Content size — how much there is to work through. */}
                    {(qCount > 0 || vCount > 0) && (
                        <div
                            className={cn(
                                "flex flex-wrap items-center gap-x-3 gap-y-1.5",
                                compact ? "mb-1" : "mb-2"
                            )}
                        >
                            {qCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <BookOpen className="h-3 w-3" aria-hidden="true" />
                                    {t("common.count.question", { count: qCount, defaultValue: qCount === 1 ? "{{count}} question" : "{{count}} questions" })}
                                </span>
                            )}
                            {vCount > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <Languages className="h-3 w-3" aria-hidden="true" />
                                    {t("common.count.vocab", { count: vCount, defaultValue: "{{count}} vocab" })}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Sessions with no questions yet have nothing to count, so
                        without this the row collapses and leaves a hole between
                        the title and the author footer. Say it plainly instead.
                        Personal cards get the stronger "unfinished" treatment
                        below, so the quiet line steps aside for them. */}
                    {qCount === 0 && vCount === 0 && !showGenerationStatus && (
                        <div className={cn("flex flex-wrap items-center gap-x-3", compact ? "mb-1" : "mb-2")}>
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/80">
                                <PencilLine className="h-3 w-3" aria-hidden="true" />
                                {t("pages.topics.noQuestionsYet", "No questions yet")}
                            </span>
                        </div>
                    )}

                    {/* A run that stopped early is not a normal topic: say so on
                        the card, and put the way to finish it next to the words
                        rather than hiding it behind the detail page. */}
                    {showGenerationStatus && isGenerating && (
                        <div className="mb-2 inline-flex w-full items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2 py-1.5 text-xs font-medium text-primary">
                            <Loader2 className="h-3 w-3 shrink-0 animate-spin motion-reduce:hidden" aria-hidden="true" />
                            <span className="truncate">
                                {t("pages.myTopics.generatingContent", "Generating content…")}
                            </span>
                        </div>
                    )}

                    {!isGenerating && onFinishGeneration && generationState.isIncomplete && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onFinishGeneration(session);
                            }}
                            className={cn(
                                "mb-2 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left",
                                "bg-amber-500/10 text-amber-800 transition-colors hover:bg-amber-500/15",
                                "dark:bg-amber-500/10 dark:text-amber-200 dark:hover:bg-amber-500/15",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            )}
                            title={t(
                                "pages.myTopics.finishGeneratingTopic",
                                "Finish generating {{topic}}",
                                { topic: session.topic }
                            )}
                            aria-label={t(
                                "pages.myTopics.finishGeneratingTopic",
                                "Finish generating {{topic}}",
                                { topic: session.topic }
                            )}
                        >
                            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                            <span className="min-w-0 flex-1 truncate text-xs font-medium">
                                {generationGapLabel}
                            </span>
                            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider">
                                {t("pages.myTopics.finishCardAction", "Finish")}
                            </span>
                        </button>
                    )}

                    {headerLeftExtra}

                    {/* Badge extra (group name, etc.) on its own line */}
                    {badgeExtra && (
                        <div className="mb-1.5 md:mb-2">
                            {badgeExtra}
                        </div>
                    )}

                    {/* Description (if present) */}
                    {session.description && (
                        <p className={cn(
                            "text-xs leading-relaxed line-clamp-2 mb-1.5 md:mb-2",
                            "text-muted-foreground"
                        )}>
                            {session.description}
                        </p>
                    )}

                    {footerExtra}

                    {/* The two things a card can do, written down. A card that
                        silently means "start practising" is the reason the old
                        body click had to ask "Choose Mode": the object never
                        said what it was for. Practise is the one the grid is
                        for; View is the detail page. */}
                    {session.slug && (
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                            {canPractise && (
                                <Link
                                    to={`/session/${session.slug}/practice`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 rounded-sm font-semibold text-primary-text outline-none transition-colors hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                                >
                                    {t("pages.topics.practise", "Practise")}
                                    <ArrowRight className="h-3 w-3" aria-hidden="true" />
                                </Link>
                            )}
                            <Link
                                to={`/session/${session.slug}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center rounded-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                            >
                                {t("pages.topics.viewTopic", "View")}
                            </Link>
                        </div>
                    )}

                    {/* Bottom section: stats + creator + progress (anchored to bottom) */}
                    <div className="mt-auto">
                        {/* Social proof. Hidden entirely when a topic has no
                            engagement yet — a row of zeros is noise that reads
                            as "unpopular" rather than "new". */}
                        {!hideStats && (session.view_count || session.like_count || session.comment_count) ? (
                            <div className="flex items-center gap-3 mb-1 mt-2 text-stone-500 dark:text-stone-400">
                                {!!session.view_count && (
                                    <span
                                        className="inline-flex items-center gap-1 text-xs"
                                        aria-label={t("common.count.views", { count: session.view_count })}
                                    >
                                        <Eye className="h-3 w-3" aria-hidden="true" />
                                        {session.view_count}
                                    </span>
                                )}
                                {!!session.like_count && (
                                    <span
                                        className="inline-flex items-center gap-1 text-xs"
                                        aria-label={t("common.count.likes", { count: session.like_count })}
                                    >
                                        <Heart className="h-3 w-3" aria-hidden="true" />
                                        {session.like_count}
                                    </span>
                                )}
                                {!!session.comment_count && (
                                    <span
                                        className="inline-flex items-center gap-1 text-xs"
                                        aria-label={t("common.count.comments", { count: session.comment_count })}
                                    >
                                        <MessageSquare className="h-3 w-3" aria-hidden="true" />
                                        {session.comment_count}
                                    </span>
                                )}
                            </div>
                        ) : null}

                        {/* Creator with separator above */}
                        {!hideCreator && (session.creator_display_name || session.user_id) && (
                            <div className="flex items-center gap-2 border-t border-stone-200/70 pt-2 dark:border-stone-800">
                                <UserAvatar
                                    userId={session.user_id}
                                    displayName={creatorName || undefined}
                                    email={session.creator_email}
                                    avatarUrl={session.creator_avatar_url}
                                    size="sm"
                                    className="w-5 h-5 md:w-6 md:h-6"
                                />
                                <span className="min-w-0 max-w-[100px] truncate text-xs text-muted-foreground">
                                    {creatorName}
                                </span>
                                {session.created_at && (
                                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                                        <Clock className="w-3 h-3 text-stone-500 dark:text-stone-400" />
                                        <time dateTime={session.created_at} title={format.dateTime(session.created_at)}>
                                            {format.relative(session.created_at)}
                                        </time>
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Your progress through this topic. The label is the
                            point: a bare percentage doesn't say whether you
                            have barely started or nearly finished. */}
                        {typeof progress === "number" && (
                            <div className="mt-2.5">
                                <div className="mb-1 flex items-center justify-between gap-2">
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                                        {progress >= 100 ? (
                                            <>
                                                <CheckCircle2
                                                    className="h-3 w-3 text-success"
                                                    aria-hidden="true"
                                                />
                                                Completed
                                            </>
                                        ) : progress > 0 ? (
                                            "In progress"
                                        ) : (
                                            "Not started"
                                        )}
                                    </span>
                                    <span className="text-xs font-medium tabular-nums text-muted-foreground">
                                        {Math.round(progress)}%
                                    </span>
                                </div>
                                <div
                                    className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                                    role="progressbar"
                                    aria-valuenow={Math.round(progress)}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-label={`Progress through ${session.topic}`}
                                >
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-primary to-secondary transition-all duration-500 ease-out"
                                        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </>
    );
};

export default memo(TopicCard);
